import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getLatestStepup,
  getStepup,
  getStepupUserStatus,
  getAllStepup,
  getStepupLaunchHistory,
} from '@wwe-portal/web/services/client/stepup';
import { useCallback, useMemo } from 'react';
import { AxiosResponse } from 'axios';
import { addDDError } from '@wwe-portal/web/libs/analytics/datadog';
import { useGetCurrentUserId, useIsAuthenticated } from '../stores/auth-store';
import { StepupUserStatus } from '@wwe-portal/entities/stepup.type';

const QUERY_KEY_PREFIX = 'stepup';
export const useGetAllStepup = () => {
  return useQuery({
    queryKey: [QUERY_KEY_PREFIX, 'offers-id'],

    queryFn: async () => {
      try {
        const response = await getAllStepup();
        return response.data.data;
      } catch {
        return [];
      }
    },
  });
};

// Filter offer which is not stepup offer and clubcoin offer.
export const useFilterShopOffers = () => {
  const isNotShopOffer = useCallback(
    <
      T extends {
        OffersID?: string;
        Web_StepUp?: boolean;
        AlternativeCostID?: string;
      },
    >(
      offer: T
    ) => {
      return (
        offer?.OffersID == null ||
        offer.Web_StepUp ||
        offer.AlternativeCostID === 'ClubCoin'
      );
    },
    []
  );
  const filterShopOffer = useCallback(
    <T extends { OffersID?: string; Web_StepUp?: boolean }>(offers: T[]) => {
      return offers.filter((offer) => !isNotShopOffer(offer));
    },
    [isNotShopOffer]
  );

  return {
    isNotShopOffer,
    filterShopOffer,
  };
};

export const useGetLatestStepup = () => {
  return useQuery({
    queryKey: [QUERY_KEY_PREFIX, 'latest'],

    queryFn: async () => {
      const defaultValue = {
        stepup: null,
      };
      try {
        const response = await getLatestStepup();
        const stepup = response.data.data;
        if (stepup.stepsUpId == null) {
          return defaultValue;
        }
        return {
          stepup,
        };
      } catch (e) {
        addDDError(e);
        return defaultValue;
      }
    },

    refetchOnWindowFocus: false,
  });
};

const defaultHistory = {
  launchTimes: 0,
  updatedAt: new Date(0).toISOString(),
};
export const useGetStepupLaunchHistory = ({
  stepsUpId,
  slotTimer,
}: {
  stepsUpId: number | undefined;
  slotTimer: number | undefined;
}) => {
  const userId = useGetCurrentUserId();
  const isAuthenticated = useIsAuthenticated();
  return useQuery({
    queryKey: [
      QUERY_KEY_PREFIX,
      'launch-popup-history',
      { stepsUpId, slotTimer },
      { userId },
    ],

    queryFn: async () => {
      try {
        if (stepsUpId == null)
          return {
            launchHistory: null,
          };
        const response = await getStepupLaunchHistory(String(stepsUpId));
        return {
          launchHistory: {
            ...defaultHistory,
            ...response.data.data,
          },
        };
      } catch (e) {
        addDDError(e);
        return {
          launchHistory: null,
        };
      }
    },

    refetchOnWindowFocus: false,
    enabled: stepsUpId != null && isAuthenticated,
  });
};

export const useInvalidateLaunchHistory = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_PREFIX, 'launch-popup-history'],
    });
};

export const useGetStepupById = (id: number | undefined) => {
  const isAuthenticated = useIsAuthenticated();
  return useQuery({
    queryKey: [QUERY_KEY_PREFIX, 'id', id],

    queryFn: async () => {
      try {
        const response = await getStepup(String(id));
        return {
          stepup: response.data.data,
        };
      } catch (e) {
        addDDError(e);
        return {
          stepup: null,
        };
      }
    },

    enabled: isAuthenticated && id != null,
    refetchOnWindowFocus: false,
  });
};

const QUERY_KEY_USER_PREFIX = 'stepup-user';
export const useGetUserStepupStatusKey = (id: number | undefined) => {
  const isAuthenticated = useIsAuthenticated();
  const userId = useGetCurrentUserId();
  return useMemo(
    () => [QUERY_KEY_USER_PREFIX, String(id), { userId }],
    [isAuthenticated, id, userId]
  );
};

export const useGetUserStepupStatus = (id: number | undefined) => {
  const isAuthenticated = useIsAuthenticated();
  const key = useGetUserStepupStatusKey(id);
  return useQuery({
    queryKey: key,
    queryFn: async () => {
      return getStepupUserStatus(String(id));
    },
    enabled: isAuthenticated && id != null,
    refetchOnWindowFocus: false,
  });
};

export const useInvalidateStepup = () => {
  const queryClient = useQueryClient();
  return useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_PREFIX],
      }),
    [queryClient]
  );
};

export const useInvalidateStepupStatus = (id: number | undefined) => {
  const key = useGetUserStepupStatusKey(id);
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: key });
    return queryClient.getQueryData<AxiosResponse<StepupUserStatus>>(key);
  }, [queryClient, key]);
};
